import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity } from "lucide-react";
import { apiClient } from "@/lib/apiClient";
import { describeActivity } from "@/lib/activity";
import { relativeTime } from "@/lib/dates";
import Skeleton from "@/components/jea/Skeleton";

const Item = ({ event }) => {
  const nav = useNavigate();
  const { Icon, color, label } = describeActivity(event);
  return (
    <button
      type="button"
      className="w-full text-left flex items-start gap-3 py-3 border-b border-border-default last:border-b-0 hover:bg-bg-tertiary/60 rounded-md px-2 transition-colors"
      onClick={() => event.debtor_id && nav(`/debtors/${event.debtor_id}`)}
      data-testid={`activity-item-${event.id}`}
    >
      <Icon size={16} className="shrink-0 mt-0.5" style={{ color }} strokeWidth={2} />
      <div className="min-w-0 flex-1">
        <div className="text-[14px] font-medium text-white truncate">{label}</div>
        <div className="text-[12px] text-text-muted">{relativeTime(event.created_at)}</div>
      </div>
    </button>
  );
};

const ActivityFeed = ({ limit = 10, className = "" }) => {
  const nav = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get(`/activity?limit=${limit}`);
        if (!cancelled) setEvents(data.events || []);
      } catch {
        /* apiClient handles auth errors */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [limit]);

  return (
    <div
      className={`bg-bg-secondary border border-border-default rounded-[12px] p-5 ${className}`}
      data-testid="activity-feed"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-white font-bold text-[16px]">Recent Activity</div>
      </div>

      {loading ? (
        <div className="space-y-3" data-testid="activity-feed-skeleton">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex gap-3 items-start">
              <Skeleton className="h-4 w-4 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-3.5 w-3/4" />
                <Skeleton className="h-2.5 w-1/4" />
              </div>
            </div>
          ))}
        </div>
      ) : events.length === 0 ? (
        <div className="py-8 flex flex-col items-center text-center gap-2" data-testid="activity-feed-empty">
          <Activity size={22} className="text-accent-pink" />
          <div className="text-text-secondary text-[14px]">No activity yet</div>
        </div>
      ) : (
        <>
          <div>{events.map((e) => <Item key={e.id} event={e} />)}</div>
          <button
            type="button"
            onClick={() => nav("/activity")}
            className="mt-3 w-full text-center text-[13px] font-semibold text-accent-pink hover:text-accent-pink-light transition-colors"
            data-testid="activity-feed-view-all"
          >
            View All
          </button>
        </>
      )}
    </div>
  );
};

export default ActivityFeed;
